import React from 'react';
import delve from 'dlv';
import useCollapse from 'react-collapsed';

import KeyValuePair from 'components/base/KeyValuePair/KeyValuePair';
import ArtistLinks from 'components/Header/EntityMetadata/ArtistLinks';

import pluralize from 'helpers/pluralize';
import formatDate from 'helpers/formatDate';
import colors from 'styles/themes/colors';
import getEntityUrl from 'url-constructors/getEntityUrl';
import { apiTypeToEntityType } from 'helpers/mapEntityTypes';
import {
  DefaultMetaData,
  Date,
  ExpandableMetaData,
  ToggleDetailsButton,
} from '../EntityMetadata';
import shouldRenderValue from 'helpers/shouldRenderValue';
import TippyToolTip from 'components/base/TippyToolTip/TippyToolTip';

interface ITrackProps {
  entity: any;
  artists: any;
  metadata: any;
  showLabel?: boolean;
}
/** @component */
const Track: React.FC<ITrackProps> = ({
  entity,
  artists,
  metadata,
  showLabel,
}) => {
  const { id, releaseDate, is_group: isGroup, isrc_list } = entity;

  const isrcValue =
    isrc_list?.length === 1
      ? isrc_list[0]
      : isrc_list?.length > 1
      ? `${isrc_list[0]}, ...`
      : '';

  const isrcTooltipContent = isrc_list
    ? `ISRC:\n${isrc_list.join('\n')}`
    : undefined;

  const animation = { transitionDuration: '300ms' };
  const { getCollapseProps, getToggleProps, isOpen } = useCollapse({
    collapseStyles: animation,
    expandStyles: animation,
  });

  return (
    <>
      <DefaultMetaData>
        <ArtistLinks artists={artists} />

        {shouldRenderValue(releaseDate) && (
          <Date>{formatDate(releaseDate, 'day-short')}</Date>
        )}
        {!isGroup && shouldRenderValue(id) && (
          <>
            <br />
            <TippyToolTip
              enabled={isrcTooltipContent !== undefined}
              tooltipContent={isrcTooltipContent}
              placement="right"
              zIndex="99999"
              maxHeight="210px"
            >
              <span>{`ISRC: ${isrcValue}`}</span>
            </TippyToolTip>
          </>
        )}
        {metadata && (
          <ToggleDetailsButton
            {...getToggleProps()}
            data-pw="toggle-entity-metadata-details"
          >
            {isOpen ? 'Hide details' : 'Show details'}
          </ToggleDetailsButton>
        )}
      </DefaultMetaData>
      {metadata && (
        <ExpandableMetadata
          metadata={metadata}
          showLabel={showLabel}
          entity={entity}
          getCollapseProps={getCollapseProps}
        />
      )}
    </>
  );
};

const ExpandableMetadata: React.FC<{
  metadata: any;
  showLabel?: boolean;
  entity: any;
  getCollapseProps: Function;
}> = ({ metadata, showLabel, entity, getCollapseProps }) => {
  const {
    type,
    id,
    is_group: isGroup,
    parent_group_id: parentEntityId,
    group_child_count,
  } = entity;
  const {
    associated_grouped_album_count,
    associatedAlbumsExcerpt,
    associated_label_count,
    associatedLabelsExcerpt,
  } = metadata;
  const [release] = associatedAlbumsExcerpt;
  const [label] = associatedLabelsExcerpt;

  return (
    <ExpandableMetaData {...getCollapseProps()}>
      {isGroup && (
        <KeyValuePair
          dataColor={colors.DeepBlue}
          label="Versions"
          value={`${group_child_count} ${pluralize(type, group_child_count)}`}
          to={
            group_child_count === 1
              ? getEntityUrl({
                  entityType: type,
                  entityId: id,
                  topic: 'overview/view',
                  parentEntityId,
                })
              : undefined
          }
          preserveSearch={true}
        />
      )}

      {!associated_grouped_album_count && (
        <KeyValuePair dataColor={colors.DeepBlue} label="Release" value="-" />
      )}
      {associated_grouped_album_count === 1 && release && (
        <KeyValuePair
          dataColor={colors.DeepBlue}
          label="Release"
          value={delve(release, 'displayName', null)}
          to={getEntityUrl({
            entityType: apiTypeToEntityType(release.type),
            entityId: release.id,
            topic: 'overview/view',
          })}
          preserveSearch={true}
          withChevron
        />
      )}
      {associated_grouped_album_count > 1 && (
        <KeyValuePair
          dataColor={colors.DeepBlue}
          label="Releases"
          value={`Included in ${associated_grouped_album_count} releases`}
          preserveSearch={true}
        />
      )}

      {showLabel && (
        <>
          {!associated_label_count && (
            <KeyValuePair
              dataColor={colors.DeepBlue}
              label="Labels"
              value="-"
            />
          )}
          {associated_label_count === 1 && label && (
            <KeyValuePair
              dataColor={colors.DeepBlue}
              label="Label"
              value={delve(label, 'displayName', null)}
              to={getEntityUrl({
                entityType: label.type,
                entityId: label.id,
                topic: 'overview/view',
              })}
              preserveSearch={true}
              withChevron
            />
          )}
          {associated_label_count > 1 && (
            <KeyValuePair
              dataColor={colors.DeepBlue}
              label="Labels"
              value={`Marketed by ${associated_label_count} labels`}
              preserveSearch={true}
            />
          )}
        </>
      )}
    </ExpandableMetaData>
  );
};

export default Track;
